import { useState, useCallback, useEffect, useRef } from 'react';

import {
  ProjectChecklist,
  useAddProjectChecklistMutation,
  useRemoveProjectChecklistMutation,
  useUpdateProjectChecklistMutation,
  useListProjectChecklistQuery,
} from '@graphql/generated';

const EMPTY_CHECKLIST: ProjectChecklist[] = [];

interface UseProjectChecklistLogicParams {
  projectUUId: string;
  onAddCompleted?: () => void;
  beforeAddItem?: () => boolean;
}

export const useProjectChecklistLogic = ({
  projectUUId,
  onAddCompleted,
  beforeAddItem,
}: UseProjectChecklistLogicParams) => {
  const [checklist, setChecklist] = useState<ProjectChecklist[]>([]);
  const [completedChecklist, setCompletedChecklist] = useState<
    ProjectChecklist[]
  >([]);
  const [processing, setProcessing] = useState(false);
  const [editChecklistItem, setEditChecklistItem] =
    useState<ProjectChecklist>();

  const {
    data: projectChecklistData,
    loading = false,
    refetch,
  } = useListProjectChecklistQuery({
    variables: {
      projectUUId,
    },
    skip: !projectUUId,
  });

  const listProjectChecklist =
    projectChecklistData?.listProjectChecklist ?? EMPTY_CHECKLIST;

  const sortChecklist = useCallback(
    (list: ProjectChecklist[]) => {
      const completedData: ProjectChecklist[] = [];
      const unCompletedData: ProjectChecklist[] = [];
      for (const item of list) {
        if (item.completedAt) {
          completedData.push(item);
        } else {
          unCompletedData.push(item);
        }
      }
      setCompletedChecklist(completedData);
      setChecklist(unCompletedData);
    },
    [setChecklist, setCompletedChecklist]
  );

  const onAddCompletedRef = useRef(onAddCompleted);
  onAddCompletedRef.current = onAddCompleted;

  const [
    addProjectChecklist,
    { data: addProjectChecklistData, error: addProjectChecklistError },
  ] = useAddProjectChecklistMutation({
    refetchQueries: ['listProjectChecklist'],
  });

  useEffect(() => {
    if (!addProjectChecklistData) return;
    sortChecklist(
      addProjectChecklistData.addProjectChecklist as ProjectChecklist[]
    );
    setProcessing(false);
    onAddCompletedRef.current?.();
  }, [addProjectChecklistData]);

  useEffect(() => {
    if (!addProjectChecklistError) return;
    setProcessing(false);
  }, [addProjectChecklistError]);

  const doAddItem = useCallback(
    async (txtName: string) => {
      if (beforeAddItem && !beforeAddItem()) {
        return;
      }
      await addProjectChecklist({
        variables: {
          projectUUId: projectUUId,
          attributes: {
            name: txtName,
          },
        },
      });
    },
    [addProjectChecklist, projectUUId, beforeAddItem]
  );

  const [removeProjectChecklist] = useRemoveProjectChecklistMutation();

  const [
    updateProjectChecklist,
    { data: updateProjectChecklistData, error: updateProjectChecklistError },
  ] = useUpdateProjectChecklistMutation({
    refetchQueries: ['listProjectChecklist', 'getProject'],
  });

  useEffect(() => {
    if (!updateProjectChecklistData) return;
    const list = [...checklist, ...completedChecklist].map(
      (item: ProjectChecklist) =>
        item.id === updateProjectChecklistData.updateProjectChecklist.id
          ? updateProjectChecklistData.updateProjectChecklist
          : item
    );
    sortChecklist(list);
    setProcessing(false);
    if (
      updateProjectChecklistData.updateProjectChecklist.id ===
      editChecklistItem?.id
    ) {
      setEditChecklistItem(undefined);
    }
  }, [updateProjectChecklistData]);

  useEffect(() => {
    if (!updateProjectChecklistError) return;
    setProcessing(false);
    setEditChecklistItem(undefined);
  }, [updateProjectChecklistError]);

  const getMaxSortValue = useCallback((checklistItems: ProjectChecklist[]) => {
    if (!checklistItems || checklistItems.length === 0) return 0;
    return Math.max(...checklistItems.map((item) => item.sort || 0));
  }, []);

  const onDelete = useCallback(
    (item: ProjectChecklist) => {
      setProcessing(true);
      removeProjectChecklist({
        variables: {
          projectChecklistId: item.id,
        },
        refetchQueries: ['listProjectChecklist'],
        onCompleted: () => {
          const list = [...checklist].filter(
            (item1: ProjectChecklist) => item1.id !== item.id
          );
          setChecklist(list);
          const list1 = [...completedChecklist].filter(
            (item1: ProjectChecklist) => item1.id !== item.id
          );
          setCompletedChecklist(list1);
          setProcessing(false);
          setEditChecklistItem(undefined);
        },
        onError: () => {
          setProcessing(false);
          setEditChecklistItem(undefined);
        },
      });
    },
    [checklist, completedChecklist, removeProjectChecklist]
  );

  const onSelect = useCallback(
    (item: ProjectChecklist) => {
      setProcessing(true);
      let sort = 0;
      if (item.completedAt !== null) {
        sort = getMaxSortValue(checklist) + 1;
      } else {
        sort = getMaxSortValue(completedChecklist) + 1;
      }
      updateProjectChecklist({
        variables: {
          projectChecklistId: item.id,
          attributes: {
            name: item.name,
            sort: sort,
            completed: item.completedAt === null,
          },
        },
      });
    },
    [checklist, completedChecklist, getMaxSortValue, updateProjectChecklist]
  );

  return {
    // State
    checklist,
    setChecklist,
    completedChecklist,
    setCompletedChecklist,
    processing,
    setProcessing,
    editChecklistItem,
    setEditChecklistItem,

    // Query
    listProjectChecklist,
    loading,
    refetch,

    // Functions
    sortChecklist,
    getMaxSortValue,
    doAddItem,
    // Raw mutate for callers that manage their own optimistic add state
    // (mobile, TA-11020) instead of waiting on doAddItem.
    addProjectChecklist,
    onDelete,
    onSelect,
    updateProjectChecklist,
  };
};
